class SoundService {
    private static instance: SoundService;
    private audioContext: AudioContext | null = null;
    private gainNode: GainNode | null = null;

    private constructor() {}

    public static getInstance(): SoundService {
        if (!SoundService.instance) {
            SoundService.instance = new SoundService();
        }
        return SoundService.instance;
    }

    private ensureAudioContext(): AudioContext | null {
        if (typeof window === "undefined") {
            return null;
        }

        if (!this.audioContext) {
            const AudioContextClass =
                window.AudioContext ||
                (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
            this.audioContext = new AudioContextClass();
            this.gainNode = this.audioContext.createGain();
            this.gainNode.gain.value = 0;
            this.gainNode.connect(this.audioContext.destination);
        }

        return this.audioContext;
    }

    /**
     * Set dialer volume from remote config. Called by settings provider when config is loaded or updated.
     */
    public updateVolumeFromValue(volume: number) {
        this.ensureAudioContext();
        if (this.gainNode) {
            this.gainNode.gain.value = Number.isFinite(volume) ? volume : 0;
        }
    }

    private async loadSound(frequency: number): Promise<AudioBuffer | null> {
        const audioContext = this.ensureAudioContext();
        if (!audioContext) return null;

        const duration = 0.2;
        const sampleRate = audioContext.sampleRate;
        const buffer = audioContext.createBuffer(1, sampleRate * duration, sampleRate);
        const data = buffer.getChannelData(0);

        for (let i = 0; i < buffer.length; i++) {
            data[i] = Math.sin((2 * Math.PI * frequency * i) / sampleRate);
        }

        return buffer;
    }

    public async playTone(frequency: number): Promise<void> {
        const audioContext = this.ensureAudioContext();
        if (!audioContext || !this.gainNode) return;

        try {
            if (audioContext.state === "suspended") {
                await audioContext.resume();
            }

            const buffer = await this.loadSound(frequency);
            if (!buffer) {
                return;
            }

            const source = audioContext.createBufferSource();
            source.buffer = buffer;
            source.connect(this.gainNode);
            source.start();
        } catch (error) {
            console.error("Error playing tone:", error);
        }
    }

    public async playDTMF(key: string): Promise<void> {
        const frequencies: { [key: string]: number } = {
            "1": 697,
            "2": 697,
            "3": 697,
            "4": 770,
            "5": 770,
            "6": 770,
            "7": 852,
            "8": 852,
            "9": 852,
            "*": 941,
            "0": 941,
            "#": 941,
        };

        const rowFreq = frequencies[key];
        const colFreq =
            key === "*" ? 1209 : key === "0" ? 1336 : key === "#" ? 1477 : 1209 + ((parseInt(key) - 1) % 3) * 127;

        await Promise.all([this.playTone(rowFreq), this.playTone(colFreq)]);
    }

    public async setOutputDevice(deviceId: string): Promise<void> {
        const audioContext = this.ensureAudioContext();
        if (!audioContext || !("setSinkId" in audioContext)) {
            return;
        }

        try {
            // @ts-expect-error setSinkId is still experimental
            await audioContext.setSinkId(deviceId);
        } catch (error) {
            console.error("Error setting output device for sound service:", error);
        }
    }
}

export const soundService = SoundService.getInstance();
